import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Plus, Clock, Building2 } from "lucide-react";
import { SessionRangeItem } from "./SessionRangeItem";
import { cn } from "../../../lib/utils";
import { useLanguage } from "../../../contexts/LanguageContext";
import { settingsLabels } from "../labels";

interface SessionsTabProps {
  sessions: any[];
  weekdayNames: string[];
  handleAddSession: (weekday: number) => void;
  handleUpdateSession: (id: number, data: { start_time?: string; end_time?: string }) => Promise<void>;
  handleDeleteSession: (id: number) => void;
}

export function SessionsTab({
  sessions,
  weekdayNames,
  handleAddSession,
  handleUpdateSession,
  handleDeleteSession,
}: SessionsTabProps) {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].exceptions;

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col gap-6">
        {[1, 2, 3, 4, 5, 6, 0].map((wd) => {
          const daySessions = sessions.filter((s) => s.weekday === wd);
          const isOpen = daySessions.length > 0;

          return (
            <div
              key={wd}
              className={cn(
                "rounded-[2.5rem] border border-neutral-100 shadow-sm overflow-hidden transition-all duration-300 group",
                isOpen ? "bg-white" : "bg-neutral-50/50 opacity-90"
              )}
            >
              <div className={cn("px-8 py-6 border-b border-neutral-50 flex items-center justify-between", isOpen ? "bg-emerald-50/30" : "bg-neutral-100/30")}>
                <h3 className="text-sm font-bold uppercase tracking-widest text-neutral-800">{weekdayNames[wd]}</h3>
                <div
                  className={cn(
                    "px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest transition-colors",
                    isOpen ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-500"
                  )}
                >
                  {isOpen ? t.sessions.open : t.sessions.closed}
                </div>
              </div>
              <div className="p-8 space-y-6">
                {daySessions.length === 0 ? (
                  <div className="py-8 flex flex-col items-center justify-center gap-4 text-neutral-400 grayscale">
                    <div className="w-16 h-16 rounded-[2rem] bg-neutral-100 flex items-center justify-center animate-pulse">
                      <Clock className="h-8 w-8 opacity-30" />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400/60">{t.sessions.noHours}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {daySessions.map((sess) => (
                      <SessionRangeItem
                        key={sess.id}
                        sess={sess}
                        lang={lang}
                        onUpdate={handleUpdateSession}
                        onDelete={handleDeleteSession}
                      />
                    ))}
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full h-14 border-2 border-dashed border-neutral-100 rounded-[1.5rem] hover:border-emerald-500/50 hover:bg-emerald-50/30 hover:text-emerald-700 text-[10px] font-bold uppercase tracking-widest transition-all active:scale-95 shadow-none"
                  onClick={() => handleAddSession(wd)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t.sessions.addPeriod}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-emerald-50/50 backdrop-blur-sm border border-emerald-100 rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left transition-all">
        <div className="bg-white w-16 h-16 rounded-[2rem] shadow-xl shadow-emerald-200/50 flex items-center justify-center shrink-0 animate-in zoom-in-50 duration-700">
          <Building2 className="h-8 w-8 text-emerald-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-emerald-900 tracking-tight">{t.sessions.aboutTitle}</h3>
          <p className="text-sm text-emerald-800/70 font-medium leading-relaxed max-w-2xl">{t.sessions.aboutDesc}</p>
        </div>
      </div>
    </div>
  );
}
